import { useState, useEffect } from 'react';
import { doc, getDoc, query, collection, where, getDocs, limit } from 'firebase/firestore';
import useFirebase from './useFirebase';
import { getCollectionName } from '../utils/firebase';

interface Company {
    id: string;
    name: string;
    email?: string;
    website?: string;
    phone?: string;
    stripeAccountId?: string;
}

const useCompany = (connectedAccountId: string | null, companyId: string | null = null) => {
    const [company, setCompany] = useState<Company | null>(null);
    const [loading, setLoading] = useState(true);
    const { db, authReady } = useFirebase();

    useEffect(() => {
        const fetchCompany = async () => {
            console.log("Fetching company for connectedAccountId/companyId:", connectedAccountId, companyId);
            if ((!connectedAccountId && !companyId) || !db || !authReady) {
                if (!authReady) return;
                setLoading(false);
                return;
            }

            try {
                let resolvedCompany: Company | null = null;

                if (connectedAccountId) {
                    // Query companies collection where stripeAccountId matches connectedAccountId
                    const companiesRef = collection(db, getCollectionName('companies'));
                    const q = query(
                        companiesRef,
                        where('stripeAccountId', '==', connectedAccountId),
                        limit(1)
                    );
                    const querySnapshot = await getDocs(q);

                    if (!querySnapshot.empty) {
                        const companyDoc = querySnapshot.docs[0];
                        const companyData = companyDoc.data();
                        resolvedCompany = {
                            id: companyDoc.id,
                            name: companyData.name || '',
                            email: companyData.email || '',
                            website: companyData.website || '',
                            phone: companyData.phone,
                            stripeAccountId: companyData.stripeAccountId
                        };
                    }
                }

                // Fallback: look up company directly by companyId when no connected account is provided/matched
                if (!resolvedCompany && companyId) {

                    console.log("Fetching company by companyId:", companyId);
                    const companyDocRef = doc(db, getCollectionName('companies'), companyId);
                    const companyDoc = await getDoc(companyDocRef);
                    if (companyDoc.exists()) {
                        const companyData = companyDoc.data();
                        resolvedCompany = {
                            id: companyDoc.id,
                            name: companyData.name || '',
                            email: companyData.email || '',
                            website: companyData.website || '',
                            phone: companyData.phone,
                            stripeAccountId: companyData.stripeAccountId
                        };
                    }
                }

                if (resolvedCompany) {
                    console.log("Company Data:", resolvedCompany);
                    setCompany(resolvedCompany);
                } else {
                    console.log("No company found");
                    setCompany({
                        id: 'default',
                        name: '',
                        email: '',
                        website: ''
                    });
                }
            } catch (error) {
                console.error('Error fetching company:', error);
                setCompany({
                    id: 'default',
                    name: '',
                    email: '',
                    website: ''
                });
            } finally {
                setLoading(false);
            }
        };

        fetchCompany();
    }, [connectedAccountId, companyId, db, authReady]);

    return { company, loading };
};

export default useCompany;
